import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form, Modal, Spinner, Alert, Badge, ProgressBar } from "react-bootstrap";
import { FaCheck, FaFilePdf } from "react-icons/fa";
import { useAuth } from "../../AuthContext";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const API_URL = process.env.REACT_APP_API_URL || "https://apiprov.conduto.com.ec";

const Politicas = ({ formData, handleChange, nextStep, prevStep, isReadOnly = false, idFormulario }) => {
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const [formulario, setFormulario] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPdf, setCurrentPdf] = useState("");
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [deleting, setDeleting] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteCert, setDeleteCert] = useState(null);

  const fetchRespuestas = async (idFormulario) => {
    const token = localStorage.getItem("token");
    const res = await fetch(
      `${API_URL}/api/documentos/respuestas?id_formulario=${idFormulario}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      }
    );
    if (!res.ok) return [];
    return await res.json();
  };

  const handleFileUpload = async (cert, file) => {
    if (!file) return;

    if (!file.type.includes('pdf')) {
      alert('Solo se permiten archivos PDF.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 5MB permitido.');
      return;
    }

    setUploading(prev => ({ ...prev, [cert.id_pregunta]: true }));
    setUploadProgress(prev => ({ ...prev, [cert.id_pregunta]: 0 }));
    setErrorMessages(prev => ({ ...prev, [cert.id_pregunta]: null }));

    const formData = new FormData();
    formData.append("archivo", file);
    formData.append("id_pregunta", cert.id_pregunta);
    formData.append("id_formulario", idFormulario);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/api/documentos/politicas/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Error al subir archivo");
      }

      const data = await res.json();

      setCertificados(prev =>
        prev.map(c =>
          c.id_pregunta === cert.id_pregunta
            ? {
              ...c,
              archivoSubido: true,
              rutaArchivo: data.data.ruta_archivo,
              id_respuesta: data.data.id_respuesta,
              aceptado: false
            }
            : c
        )
      );

      setUploadProgress(prev => ({ ...prev, [cert.id_pregunta]: 100 }));
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[cert.id_pregunta];
          return newProgress;
        });
      }, 2000);

    } catch (err) {
      console.error("Error al subir archivo:", err);
      setErrorMessages(prev => ({
        ...prev,
        [cert.id_pregunta]: err.message || "No se pudo subir el archivo."
      }));
    } finally {
      setUploading(prev => ({ ...prev, [cert.id_pregunta]: false }));
    }
  };

  const openDeleteModal = (cert) => {
    if (!cert.id_respuesta) return;
    setDeleteCert(cert);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteCert) return;

    setDeleting(prev => ({ ...prev, [deleteCert.id_pregunta]: true }));
    setErrorMessages(prev => ({ ...prev, [deleteCert.id_pregunta]: null }));

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/documentos/respuestas/${deleteCert.id_respuesta}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });

      if (res.ok) {
        setCertificados(prev =>
          prev.map(c =>
            c.id_pregunta === deleteCert.id_pregunta
              ? {
                ...c,
                archivoSubido: false,
                rutaArchivo: null,
                aceptado: false,
                id_respuesta: c.id_respuesta
              }
              : c
          )
        );
        setShowDeleteConfirm(false);
        setDeleteCert(null);
      } else {
        const error = await res.json();
        setErrorMessages(prev => ({
          ...prev,
          [deleteCert.id_pregunta]: error.detail || "No se pudo eliminar el archivo."
        }));
      }
    } catch (err) {
      console.error("Error al eliminar archivo:", err);
      setErrorMessages(prev => ({
        ...prev,
        [deleteCert.id_pregunta]: "Error al eliminar el archivo."
      }));
    } finally {
      setDeleting(prev => ({ ...prev, [deleteCert.id_pregunta]: false }));
    }
  };

  const toggleAceptacion = async (cert) => {
    if (isReadOnly) return;

    const nuevo = !cert.aceptado;
    const token = localStorage.getItem("token");

    setErrorMessages(prev => ({ ...prev, [cert.id_pregunta]: null }));

    try {
      let idRespuesta = cert.id_respuesta;

      if (!idRespuesta && nuevo) {
        const formData = new FormData();
        formData.append("id_pregunta", cert.id_pregunta);
        formData.append("id_formulario", idFormulario);
        formData.append("respuesta_texto", "Aceptado por el usuario");

        const res = await fetch(`${API_URL}/api/documentos/respuestas/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: formData,
        });

        if (!res.ok) throw new Error("Error al crear respuesta");
        const data = await res.json();
        idRespuesta = data.data.id_respuesta;

        setCertificados((prev) =>
          prev.map((c) =>
            c.id_pregunta === cert.id_pregunta
              ? { ...c, id_respuesta: idRespuesta }
              : c
          )
        );
      }

      if (!idRespuesta && !nuevo) {
        setCertificados((prev) =>
          prev.map((c) =>
            c.id_pregunta === cert.id_pregunta
              ? { ...c, aceptado: false }
              : c
          )
        );
        return;
      }

      const res = await fetch(
        `${API_URL}/api/documentos/politicas/${idRespuesta}/aceptar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            aceptado: nuevo,
            requiere_archivo: cert.requiere_archivo,
            tiene_archivo: cert.archivoSubido || false
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        if (res.status === 400 && errorData.detail.includes("requiere un archivo")) {
          setErrorMessages(prev => ({
            ...prev,
            [cert.id_pregunta]: "Esta política requiere que subas un documento antes de aceptarla."
          }));
          return;
        }
        throw new Error(errorData.detail || "Error al guardar aceptación");
      }

      setCertificados((prev) =>
        prev.map((c) =>
          c.id_pregunta === cert.id_pregunta ? { ...c, aceptado: nuevo } : c
        )
      );
    } catch (err) {
      setErrorMessages(prev => ({
        ...prev,
        [cert.id_pregunta]: err.message || "No se pudo guardar tu aceptación."
      }));
    }
  };

  const toggleSection = (idPregunta) =>
    setCertificados((prev) =>
      prev.map((c) =>
        c.id_pregunta === idPregunta ? { ...c, expanded: !c.expanded } : c
      )
    );

  const showPdfModal = (url) => {
    setCurrentPdf(url);
    setShowModal(true);
  };

  const getFileUrl = (rutaArchivo) => {
    if (!rutaArchivo) return "#";
    const rutaCodificada = encodeURIComponent(rutaArchivo);
    return `${API_URL}/api/documentos/visualizar/${rutaCodificada}`;
  };

  const shortenFileName = (fileName, maxLength = 20) => {
    if (!fileName) return 'Seleccionar archivo';
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.length - extension.length - 1);
    const shortenedName = nameWithoutExt.substring(0, maxLength - extension.length - 3) + '...';
    return `${shortenedName}.${extension}`;
  };

  useEffect(() => {
    if (!user?.id_usuario) return;
    let cancelled = false;

    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        let formData = null;
        let idFormularioGeneral = null;

        if (idFormulario) {
          const res = await fetch(
            `${API_URL}/api/providers/form-data/${idFormulario}`,
            { headers, credentials: "include" }
          );
          if (res.ok) {
            formData = await res.json();
            idFormularioGeneral = formData.formulario.id_formulario_general;
          }
        } else {
          const resActivo = await fetch(`${API_URL}/api/providers/active-form`, {
            headers,
            credentials: "include",
          });

          if (resActivo.ok) {
            formData = await resActivo.json();
            idFormularioGeneral = formData.formulario.id_formulario_general;
          } else {
            const resFin = await fetch(
              `${API_URL}/api/formularios/respuestas/ultimo-finalizado`,
              { headers, credentials: "include" }
            );
            if (resFin.ok) {
              const f = await resFin.json();
              formData = { id_formulario: f.id_formulario, estado: "finalizado" };
              idFormularioGeneral = f.id_formulario_general;
            }
          }
        }

        if (!formData) {
          if (!cancelled) {
            setError("No hay formularios activos ni finalizados.");
            setLoading(false);
          }
          return;
        }

        if (!cancelled) setFormulario(formData);

        let preguntasUrl = `${API_URL}/api/documentos/preguntas?tipo=certificado&tipo_persona=${user.tipo_persona}&activo=true`;

        if (idFormularioGeneral) {
          preguntasUrl += `&id_formulario_general=${idFormularioGeneral}`;
        }

        const [certs, respuestas] = await Promise.all([
          fetch(preguntasUrl, { headers, credentials: "include" })
            .then((r) => (r.ok ? r.json() : [])),
          fetchRespuestas(idFormulario || formData.id_formulario),
        ]);

        if (cancelled) return;

        const mapped = certs.map((cert) => {
          const r = respuestas.find((re) => re.id_pregunta === cert.id_pregunta);
          const tieneArchivo = !!r?.ruta_archivo;
          const estaAceptado = Boolean(r?.aceptado);
          const requiereArchivo = cert.requiere_archivo === 1 || cert.requiere_archivo === true;

          let aceptadoFinal = estaAceptado;

          if (requiereArchivo && !tieneArchivo && estaAceptado) {
            aceptadoFinal = false;
            console.log(`Política ${cert.titulo}: Requiere archivo pero no tiene archivo. Desmarcando aceptación.`);
          }

          return {
            ...cert,
            expanded: false,
            respuesta: r || null,
            id_respuesta: r?.id_respuesta || null,
            archivoSubido: tieneArchivo,
            rutaArchivo: r?.ruta_archivo || null,
            archivoModelo: cert.ruta_archivo
              ? `${API_URL}/uploads/${cert.ruta_archivo}`
              : null,
            aceptado: aceptadoFinal,
            requiere_archivo: requiereArchivo
          };
        });

        setCertificados(mapped);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => (cancelled = true);
  }, [user, idFormulario]);

  const allCompleted = certificados.every((c) => {
    if (!c.obligatorio) return true;
    if (!c.aceptado) return false;
    if (c.requiere_archivo && !c.archivoSubido) return false;
    return true;
  });

  if (loading)
    return (
      <div className="p-3 text-center">
        <Spinner animation="border" />
        <p>Cargando políticas...</p>
      </div>
    );

  if (error)
    return (
      <div className="p-3">
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      </div>
    );

  return (
    <div className="p-3">
      <h5 className="mb-4">Lista de políticas</h5>

      {!certificados.length ? (
        <Alert variant="info">
          No hay políticas para{" "}
          {user.tipo_persona === "juridica" ? "persona jurídica" : "persona natural"} en este momento.
        </Alert>
      ) : (
        certificados.map((cert) => (
          <div
            key={cert.id_pregunta}
            className={`mb-3 border rounded p-3 ${cert.aceptado ? "bg-light-success" : ""}`}
            style={{
              backgroundColor: cert.aceptado ? "rgba(40, 167, 69, 0.1)" : "",
              transition: "background-color 0.3s ease",
              position: "relative",
            }}
          >
            {cert.aceptado && (
              <div
                style={{
                  position: "absolute",
                  left: "-10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#28a745",
                  color: "white",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaCheck size={12} />
              </div>
            )}

            <div
              className="d-flex justify-content-between align-items-center"
              onClick={() => toggleSection(cert.id_pregunta)}
              style={{ cursor: "pointer" }}
            >
              <h6 className="mb-0" style={{ marginLeft: cert.aceptado ? "20px" : "0" }}>
                {cert.titulo} {cert.obligatorio && <span className="text-danger">*</span>}
                {cert.requiere_archivo && (
                  <Badge bg="warning" className="ms-2" style={{ fontSize: "0.7rem" }}>
                    Requiere documento
                  </Badge>
                )}
              </h6>
              <span className="text-muted">{cert.expanded ? "▲" : "▼"}</span>
            </div>

            {cert.expanded && (
              <div className="mt-3">
                <p className="text-muted">{cert.descripcion}</p>

                {cert.archivoModelo && (
                  <div className="mb-3">
                    <Button
                      style={{ background: "#00457E", color: "white", border: "none" }}
                      variant="outline-info"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        showPdfModal(cert.archivoModelo);
                      }}
                    >
                      Mostrar política
                    </Button>
                  </div>
                )}

                {cert.requiere_archivo && !isReadOnly && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center">
                      {cert.archivoSubido ? (
                        <div className="d-flex align-items-center">
                          <FaFilePdf className="text-danger me-2" size={20} />
                          <a
                            href={getFileUrl(cert.rutaArchivo)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="me-3 text-truncate text-decoration-none"
                            style={{ maxWidth: "200px" }}
                          >
                            {shortenFileName(cert.rutaArchivo ? cert.rutaArchivo.split('/').pop() : 'documento.pdf')}
                          </a>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(cert);
                            }}
                            disabled={deleting[cert.id_pregunta]}
                          >
                            {deleting[cert.id_pregunta] ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              'Eliminar'
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="w-100">
                          <div className="input-group">
                            <input
                              type="file"
                              id={`file-${cert.id_pregunta}`}
                              className="form-control d-none"
                              accept=".pdf"
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.files[0]) {
                                  handleFileUpload(cert, e.target.files[0]);
                                }
                                e.target.value = "";
                              }}
                              disabled={uploading[cert.id_pregunta]}
                            />
                            <label htmlFor={`file-${cert.id_pregunta}`} className="btn btn-outline-primary flex-grow-1">
                              {uploading[cert.id_pregunta] ? (
                                <>
                                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                                  <span>Subiendo... {uploadProgress[cert.id_pregunta]}%</span>
                                </>
                              ) : (
                                'Subir documento PDF'
                              )}
                            </label>
                          </div>
                          {uploading[cert.id_pregunta] && uploadProgress[cert.id_pregunta] > 0 && (
                            <ProgressBar
                              now={uploadProgress[cert.id_pregunta]}
                              className="mt-2"
                              variant="success"
                              animated
                              label={`${uploadProgress[cert.id_pregunta]}%`}
                            />
                          )}
                          <Form.Text className="text-muted">
                            Solo archivos PDF. Máximo 5MB.
                          </Form.Text>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isReadOnly ? (
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={cert.aceptado ? "success" : "secondary"}>
                      {cert.aceptado ? "Aceptado" : "No aceptado"}
                    </Badge>
                    {cert.requiere_archivo && (
                      <Badge bg={cert.archivoSubido ? "success" : "danger"}>
                        {cert.archivoSubido ? "Documento adjunto" : "Documento faltante"}
                      </Badge>
                    )}
                    {cert.aceptado && <FaCheck className="text-success" />}
                  </div>
                ) : (
                  <>
                    {errorMessages[cert.id_pregunta] && (
                      <div className="mb-2 p-2 bg-danger text-white rounded" style={{ fontSize: "0.85rem" }}>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {errorMessages[cert.id_pregunta]}
                      </div>
                    )}

                    <Form.Check
                      type="checkbox"
                      id={`accept-checkbox-${cert.id_pregunta}`}
                      label="Dejo constancia de haber leído y aceptado las políticas establecidas por la organización"
                      checked={cert.aceptado}
                      disabled={isReadOnly || (cert.requiere_archivo && !cert.archivoSubido)}
                      onChange={() => toggleAceptacion(cert)}
                    />

                    {cert.requiere_archivo && !cert.archivoSubido && (
                      <div className="mt-2 text-danger" style={{ fontSize: "0.8rem" }}>
                        <i className="bi bi-info-circle-fill me-1"></i>
                        Debes subir un documento PDF antes de aceptar esta política.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Visualizador de Política</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ratio ratio-16x9">
            <iframe
              src={`${currentPdf}#view=fitH`}
              title="PDF Viewer"
              style={{ border: "none" }}
              loading="lazy"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setDeleteCert(null);
        }}
        onConfirm={confirmDelete}
        title="¿Está seguro de que desea eliminar este archivo?"
        message="Al eliminar el archivo, la política quedará sin aceptar y deberá volver a subir el documento."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={deleteCert && deleting[deleteCert.id_pregunta]}
        icon={<i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>}
      />

      <div className="d-flex justify-content-between mt-4">
        <Button variant="outline-secondary" onClick={prevStep}>
          Anterior
        </Button>
        {!isReadOnly && (
          <Button
            variant="primary"
            onClick={nextStep}
            disabled={!allCompleted}
            title={!allCompleted ? "Debes aceptar todas las políticas obligatorias y subir los documentos requeridos" : ""}
          >
            Siguiente
          </Button>
        )}
      </div>
    </div>
  );
};

export default Politicas;